/**
 * Leaving the dashboard for a Stripe-hosted page: Checkout, or the customer
 * portal. The server (routes/billing.ts) creates the session and returns its
 * URL; this sends the whole tab there.
 *
 * The tab navigates ONLY on a URL the server actually returned. Every other
 * outcome resolves to a CODE and leaves the reader where they are, so the
 * caller can say something true instead of going blank.
 *
 * Pure on purpose: no logger and no apiBaseUrl import (both read
 * import.meta.env, which ts-jest cannot parse), so this file is tested for
 * real and the hook that wraps it supplies both.
 */
export type StripeRedirectTarget =
  | { kind: 'checkout'; planSlug: string; period: 'month' | 'annual'; extraSeats?: number }
  | { kind: 'portal' };

export type StripeRedirectResult = { ok: true } | { ok: false; code: string };

const defaultNavigateTo = (url: string): void => window.location.assign(url);

export async function openStripeRedirect(opts: {
  apiBase: string;
  teamId: string | undefined;
  getToken: () => Promise<string | null>;
  target: StripeRedirectTarget;
  navigateTo?: (url: string) => void;
}): Promise<StripeRedirectResult> {
  const { apiBase, teamId, getToken, target, navigateTo = defaultNavigateTo } = opts;
  if (!teamId) return { ok: false, code: 'NO_TEAM' };

  const route = target.kind === 'checkout' ? 'checkout-session' : 'portal-session';
  const body =
    target.kind === 'checkout' ? { teamId, planSlug: target.planSlug, period: target.period, extraSeats: target.extraSeats } : { teamId };

  let res: Response;
  try {
    const token = await getToken();
    res = await fetch(`${apiBase}/api/billing/${route}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, code: 'NETWORK' };
  }

  const json = (await res.json().catch(() => null)) as
    | { data?: { url?: unknown }; error?: { code?: unknown } }
    | null;

  if (!res.ok) {
    const code = json?.error?.code;
    return { ok: false, code: typeof code === 'string' && code ? code : 'BAD_RESPONSE' };
  }

  const url = json?.data?.url;
  if (typeof url !== 'string' || !url) return { ok: false, code: 'BAD_RESPONSE' };

  navigateTo(url);
  return { ok: true };
}

/**
 * What the reader is told when a Stripe page could not be opened. Two refusals
 * are something they can act on, so they are named; everything else (our
 * configuration, Stripe being down, the network) is one honest sentence,
 * because none of it is theirs to fix.
 */
export function stripeRedirectErrorCopy(code: string): string {
  switch (code) {
    case 'ALREADY_SUBSCRIBED':
      return 'This workspace already has an active subscription.';
    case 'NOT_OWNER':
      return 'Only the team owner can manage the subscription.';
    case 'SEATS_BELOW_MEMBERS':
      return 'Your team needs more seats. Refresh the page to see the current minimum, then try again.';
    case 'EXTRA_SEATS_UNAVAILABLE':
      return 'Extra seats are unavailable right now. Your selection is saved. Please try again later.';
    default:
      return "We couldn't open the checkout just now. Please try again in a moment.";
  }
}
